import { useApiRequest } from 'hooks/useApiRequest';
// Hooks
import useValidation from 'hooks/useValidation';
import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { i18n as I18n } from 'utils/i18n';
import type { Schema } from 'yup';
import * as yup from 'yup';
import { PASSWORD_REGEX } from '../../constants/password';
import type { FieldChangeEvent } from '../../types';
import { phoneNumber, validateXSS, xssErrorMessage } from '../../validators';

// The user-details half of the registration/profile forms.
export interface UserDetailsFields {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  mobileNumber?: string;
  email?: string;
  confirmEmail?: string;
  password?: string;
  confirmPassword?: string;
  // See types/registration: checkbox events store ids as strings.
  marketingOptions?: Array<number | string>;
}

// A marketing preference the user can opt in to.
export interface MarketingOption {
  id: number;
  name: string;
}

interface UseUserDetailsFormHookOptions {
  fields: UserDetailsFields;
  handleFieldChange: (event: FieldChangeEvent) => void;
  handleSubmitCallback?: () => void;
  // Per-screen additions/replacements merged into the schema below (e.g. the
  // profile form drops the password rules).
  schemaOverRides?: Record<string, Schema>;
  validateEmailUniqueness?: boolean;
}

export const useUserDetailsFormHook = ({
  fields,
  handleFieldChange,
  handleSubmitCallback = () => null,
  schemaOverRides,
  validateEmailUniqueness,
}: UseUserDetailsFormHookOptions) => {
  const schema = useMemo(() => {
    return yup.object().shape({
      firstName: yup
        .string()
        .required(
          I18n.t('validations.required', {
            name: I18n.t('registration.user_details.fields.first_name.label'),
          })
        )
        .test('no-xss', xssErrorMessage, value => validateXSS(value as string)),
      lastName: yup
        .string()
        .required(
          I18n.t('validations.required', {
            name: I18n.t('registration.user_details.fields.last_name.label'),
          })
        )
        .test('no-xss', xssErrorMessage, value => validateXSS(value as string)),
      phoneNumber: phoneNumber(
        I18n.t('registration.user_details.fields.phone_number.label')
      ),
      mobileNumber: phoneNumber(
        I18n.t('registration.user_details.fields.mobile_number.label')
      ),
      email: yup
        .string()
        .email(
          I18n.t('validations.email', {
            name: I18n.t('registration.user_details.fields.email.label'),
          })
        )
        .test('no-xss', xssErrorMessage, value => validateXSS(value as string))
        .required(
          I18n.t('validations.required', {
            name: I18n.t('registration.user_details.fields.email.label'),
          })
        ),
      confirmEmail: yup
        .string()
        .email(
          I18n.t('validations.email', {
            name: I18n.t(
              'registration.user_details.fields.confirm_email.label'
            ),
          })
        )
        .test('no-xss', xssErrorMessage, value => validateXSS(value as string))
        .required(
          I18n.t('validations.required', {
            name: I18n.t(
              'registration.user_details.fields.confirm_email.label'
            ),
          })
        )
        .test(
          'matchesEmail',
          I18n.t('validations.match', {
            name: I18n.t(
              'registration.user_details.fields.confirm_email.label'
            ),
          }),
          /* eslint-disable func-names */
          function (confirmValue) {
            const value = this.resolve(yup.ref('email'));
            return !value || !confirmValue || value === confirmValue;
          }
          /* eslint-enable func-names */
        ),
      password: yup
        .string()
        .required(
          I18n.t('validations.required', {
            name: I18n.t('registration.user_details.fields.password.label'),
          })
        )
        .test('no-xss', xssErrorMessage, value => validateXSS(value as string))
        .matches(
          PASSWORD_REGEX,
          I18n.t('validations.password_security', {
            name: I18n.t('registration.user_details.fields.password.label'),
            length: 10,
          })
        ),
      confirmPassword: yup
        .string()
        .required(
          I18n.t('validations.required', {
            name: I18n.t(
              'registration.user_details.fields.confirm_password.label'
            ),
          })
        )
        .test('no-xss', xssErrorMessage, value => validateXSS(value as string))
        .test(
          'matchesPassword',
          I18n.t('validations.match', {
            name: I18n.t(
              'registration.user_details.fields.confirm_password.label'
            ),
          }),
          /* eslint-disable func-names */
          function (confirmValue) {
            const value = this.resolve(yup.ref('password'));
            return !value || !confirmValue || value === confirmValue;
          }
          /* eslint-enable func-names */
        ),
      marketingOptions: yup.array().of(yup.string()),
      ...schemaOverRides,
    });
  }, [schemaOverRides]);

  const {
    errors,
    setErrors,
    handleChange: handleFieldValidation,
    isValid,
    validate,
  } = useValidation(fields, schema);

  const [userIsValid, setUserIsValid] = useState(false);
  const [emailIsUnregistered, setEmailIsUnregistered] = useState(false);
  const [marketingOptions, setMarketingOptions] = useState<MarketingOption[]>(
    []
  );

  const apiRequest = useApiRequest();

  useEffect(() => {
    const fetchMarketingOptions = async () => {
      if (marketingOptions.length === 0) {
        const { data } = await apiRequest.get(
          '/v1/organisations/marketing_options'
        );
        setMarketingOptions(data);
      }
    };

    fetchMarketingOptions();
  }, [marketingOptions, apiRequest]);

  const checkMatch = useCallback(
    (fieldName: string, value?: string, checkValue?: string) => {
      if (value !== '' && checkValue !== '') {
        handleFieldValidation({
          currentTarget: {
            name: fieldName,
            value: value ?? '',
          },
        });
      }
    },
    [handleFieldValidation]
  );

  useEffect(() => {
    checkMatch('email', fields.email, fields.confirmEmail);
  }, [checkMatch, fields.email, fields.confirmEmail]);

  useEffect(() => {
    checkMatch('confirmEmail', fields.confirmEmail, fields.email);
  }, [checkMatch, fields.email, fields.confirmEmail]);

  useEffect(() => {
    checkMatch('password', fields.password, fields.confirmPassword);
  }, [checkMatch, fields.password, fields.confirmPassword]);

  useEffect(() => {
    checkMatch('confirmPassword', fields.confirmPassword, fields.password);
  }, [checkMatch, fields.password, fields.confirmPassword]);

  const checkUniqueEmail = useCallback(
    (email?: string) => {
      if (isValid()) {
        const emailIsRegistered = async () => {
          await apiRequest.get('/v1/users/email/', { params: { find: email } });
        };

        emailIsRegistered()
          .then(() => {
            setErrors(prev => ({
              ...prev,
              email: I18n.t('validations.email_registered'),
            }));
            setEmailIsUnregistered(false);
          })
          .catch(() => {
            setEmailIsUnregistered(true);
          });
      }
    },
    [isValid, apiRequest, setErrors]
  );

  useEffect(() => {
    if (validateEmailUniqueness) {
      checkUniqueEmail(fields.email);
    }
  }, [fields, checkUniqueEmail, validateEmailUniqueness]);

  useEffect(() => {
    if (validateEmailUniqueness) {
      setUserIsValid(isValid() && emailIsUnregistered);
    } else {
      setUserIsValid(isValid());
    }
  }, [isValid, emailIsUnregistered, validateEmailUniqueness]);

  const handleChange = (event: FieldChangeEvent) => {
    handleFieldChange(event);
    handleFieldValidation(event);
  };

  const handlePasswordChange = (value: string) => {
    const event = { currentTarget: { name: 'password', value } };
    handleFieldChange(event);
    handleFieldValidation(event);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (isValid()) {
      handleSubmitCallback();
    }

    return false;
  };

  return {
    errors,
    handleSubmit,
    handlePasswordChange,
    handleChange,
    checkMatch,
    marketingOptions,
    isValid: userIsValid,
    isFieldsValid: isValid,
    validate,
  };
};
